import type { ReactNode } from 'react';
import { cn } from '../../lib/cn';
import type { StatusTone } from './StatusText';

/**
 * A quiet inline label for the facts that qualify a record rather than alert about it: the template and
 * policy version on an estimate, "Stale", "Provisional", a document category. It follows the same colour
 * discipline as `StatusText` (plan §2.6) — `muted` by default, colour only when it must be noticed.
 */
export type BadgeTone = StatusTone;

const TONE_CLASSES: Record<BadgeTone, string> = {
  muted: 'border-border bg-muted text-muted-foreground',
  danger: 'border-destructive/30 bg-destructive/5 text-destructive',
  warning: 'border-[#a16207]/30 bg-[#a16207]/5 text-[#a16207]',
  success: 'border-[#15803d]/30 bg-[#15803d]/5 text-[#15803d]',
  info: 'border-[#2563eb]/30 bg-[#2563eb]/5 text-[#2563eb]',
};

export interface BadgeProps {
  children: ReactNode;
  tone?: BadgeTone;
  /** Mono for codes and version numbers — `v3`, `UC-000301`. */
  mono?: boolean;
  className?: string;
}

export const Badge = ({ children, tone = 'muted', mono = false, className }: BadgeProps) => (
  <span
    className={cn(
      'inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-medium',
      TONE_CLASSES[tone],
      mono && 'font-mono',
      className,
    )}
  >
    {children}
  </span>
);
